/**
 * PlatformAdminAuthContext
 *
 * Separate authentication context for Platform Admin panel.
 * Uses different token storage than main app to allow independent sessions.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface PlatformAdminUser {
  id: string;
  email: string;
  name?: string;
  is_superuser: boolean;
}

interface PlatformAdminAuthContextType {
  user: PlatformAdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const PlatformAdminAuthContext = createContext<PlatformAdminAuthContextType | undefined>(undefined);

const PLATFORM_ADMIN_TOKEN_KEY = 'platform_admin_access_token';
const PLATFORM_ADMIN_REFRESH_KEY = 'platform_admin_refresh_token';
const PLATFORM_ADMIN_USER_KEY = 'platform_admin_user';

export const PlatformAdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PlatformAdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount - trust localStorage, 401 handlers will catch invalid tokens
  useEffect(() => {
    const storedToken = localStorage.getItem(PLATFORM_ADMIN_TOKEN_KEY);
    const storedUser = localStorage.getItem(PLATFORM_ADMIN_USER_KEY);

    console.log('[PlatformAdminAuth] Checking auth:', {
      hasToken: !!storedToken,
      hasUser: !!storedUser,
      tokenPreview: storedToken ? storedToken.substring(0, 20) + '...' : null
    });

    if (storedToken && storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed.is_superuser) {
          setUser(parsed);
        } else {
          // Not a superuser - clear auth
          localStorage.removeItem(PLATFORM_ADMIN_TOKEN_KEY);
          localStorage.removeItem(PLATFORM_ADMIN_REFRESH_KEY);
          localStorage.removeItem(PLATFORM_ADMIN_USER_KEY);
        }
      } catch {
        localStorage.removeItem(PLATFORM_ADMIN_USER_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch('/api/v1/auth/login/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || data.error?.message || 'Invalid credentials');
    }

    const data = await response.json();

    // Check if user is superuser
    if (!data.user?.is_superuser) {
      throw new Error('Access denied. SuperUser privileges required.');
    }

    // Store tokens separately from main app
    console.log('[PlatformAdminAuth] Login successful, storing tokens:', {
      hasAccessToken: !!data.access_token,
      hasRefreshToken: !!data.refresh_token,
      user: data.user?.email,
      is_superuser: data.user?.is_superuser
    });

    localStorage.setItem(PLATFORM_ADMIN_TOKEN_KEY, data.access_token);
    localStorage.setItem(PLATFORM_ADMIN_REFRESH_KEY, data.refresh_token);
    localStorage.setItem(PLATFORM_ADMIN_USER_KEY, JSON.stringify(data.user));

    setUser({
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      is_superuser: data.user.is_superuser,
    });
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem(PLATFORM_ADMIN_TOKEN_KEY);
    localStorage.removeItem(PLATFORM_ADMIN_REFRESH_KEY);
    localStorage.removeItem(PLATFORM_ADMIN_USER_KEY);
    setUser(null);
  }, []);

  const value: PlatformAdminAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <PlatformAdminAuthContext.Provider value={value}>
      {children}
    </PlatformAdminAuthContext.Provider>
  );
};

export const usePlatformAdminAuth = (): PlatformAdminAuthContextType => {
  const context = useContext(PlatformAdminAuthContext);
  if (!context) {
    throw new Error('usePlatformAdminAuth must be used within PlatformAdminAuthProvider');
  }
  return context;
};

// Helper to get token for API calls
export const getPlatformAdminToken = (): string | null => {
  return localStorage.getItem(PLATFORM_ADMIN_TOKEN_KEY);
};

// Helper to handle 401 errors - clears auth and redirects to login
export const handlePlatformAdmin401 = (): void => {
  console.log('[PlatformAdminAuth] 401 received - clearing auth and redirecting to login');
  localStorage.removeItem(PLATFORM_ADMIN_TOKEN_KEY);
  localStorage.removeItem(PLATFORM_ADMIN_REFRESH_KEY);
  localStorage.removeItem(PLATFORM_ADMIN_USER_KEY);
  window.location.href = '/platform-admin/login';
};

export default PlatformAdminAuthContext;
